import { useCallback, useEffect, useState } from 'react';
import { FavouriteItem, MAX_FAVOURITES } from '@/interfaces/Favourite.interface';
import useStore from '@/hooks/useStore';
import {
  favouritesStorageKey,
  readFavourites,
  subscribeFavourites,
  toggleFavouriteInList,
  writeFavourites
} from '@/utils/favouritesStorage';

export function useFavourites() {
  const { authStore, toast } = useStore();
  const userId = authStore.user?.id;

  const [favourites, setFavourites] = useState<FavouriteItem[]>(() =>
    readFavourites(userId)
  );

  useEffect(() => {
    setFavourites(readFavourites(userId));
  }, [userId]);

  useEffect(() => {
    if (userId == null) return undefined;

    const unsubscribe = subscribeFavourites((next) => {
      setFavourites(next);
    });

    const onStorage = (event: StorageEvent) => {
      if (event.key !== favouritesStorageKey(userId)) return;
      setFavourites(readFavourites(userId));
    };

    window.addEventListener('storage', onStorage);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  const isFavourite = useCallback(
    (id: string) => favourites.some((item) => item.id === id),
    [favourites]
  );

  const removeFavourite = useCallback(
    (id: string) => {
      if (userId == null) return;
      const next = favourites.filter((item) => item.id !== id);
      setFavourites(writeFavourites(userId, next));
    },
    [favourites, userId]
  );

  const toggleFavourite = useCallback(
    (item: Omit<FavouriteItem, 'createdAt'> & { createdAt?: number }) => {
      if (userId == null) return false;
      const result = toggleFavouriteInList(favourites, item);
      if (!result.ok) {
        toast.error(
          `You can only favourite up to ${MAX_FAVOURITES} items. Remove one to add another.`
        );
        return false;
      }
      setFavourites(writeFavourites(userId, result.favourites));
      return true;
    },
    [favourites, toast, userId]
  );

  return {
    favourites,
    isFavourite,
    toggleFavourite,
    removeFavourite,
    maxFavourites: MAX_FAVOURITES
  };
}

export default useFavourites;
